import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import type { ScrapMixFilterOption } from "../models";
import {
  ALL_OPTIONS_LABEL,
  areAllOptionsSelected,
} from "../utils/filterSelection";

interface Props {
  label: string;
  options: ScrapMixFilterOption[];
  values: string[];
  onChange: (values: string[]) => void;
  testId: string;
  menuWidth?: number;
  optionNoWrap?: boolean;
}

export const CompactMultiSelect = ({
  label,
  options,
  values,
  onChange,
  testId,
  menuWidth,
  optionNoWrap = false,
}: Props) => {
  const selectedOptions = useMemo(() => {
    const optionsByValue = new Map(
      options.map((option) => [option.Value, option]),
    );
    return values.map(
      (value) =>
        optionsByValue.get(value) ?? {
          FilterType: options[0]?.FilterType ?? "Heat",
          Value: value,
          Label: value,
        },
    );
  }, [options, values]);

  const allSelected = areAllOptionsSelected(options, values);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={options}
      value={selectedOptions}
      getOptionLabel={(option) => option.Label}
      isOptionEqualToValue={(option, value) => option.Value === value.Value}
      onChange={(_, selected) => onChange(selected.map((item) => item.Value))}
      renderOption={(props, option, state) => (
        <Box component="li" {...props} key={option.Value}>
          <Checkbox checked={state.selected} size="small" sx={{ mr: 1 }} />
          <Typography variant="body2" noWrap={optionNoWrap}>
            {option.Label}
          </Typography>
        </Box>
      )}
      renderTags={(selected) => (
        <Chip
          size="small"
          label={
            allSelected
              ? ALL_OPTIONS_LABEL
              : selected.length === 1
                ? selected[0].Label
                : `${selected.length} selected`
          }
          onDelete={() => onChange([])}
          sx={{ maxWidth: "calc(100% - 4px)" }}
        />
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={values.length === 0 ? `${label}: ${ALL_OPTIONS_LABEL}` : label}
          size="small"
          fullWidth
          inputProps={{ ...params.inputProps, "data-testid": testId }}
        />
      )}
      slotProps={{
        popper: menuWidth ? { sx: { minWidth: menuWidth } } : undefined,
      }}
      fullWidth
    />
  );
};
